import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Check, Link2, Unplug, RefreshCw } from "lucide-react";

/**
 * "Connect Google Calendar" panel. Shows the current sync status + a Connect
 * button that redirects to Google OAuth. On return the URL carries ?gcal=connected
 * or ?gcal=error and we surface a toast + reload status.
 */
export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState({ loading: true, connected: false, configured: false });
  const [calendars, setCalendars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    // /gcal/status is the source of truth for `configured` and `connected`.
    // A failure there means the server itself is unreachable — keep the
    // previous state rather than incorrectly flipping to "not configured".
    let st;
    try {
      const { data } = await api.get("/gcal/status");
      st = data;
      setStatus({ loading: false, ...data });
    } catch (_e) {
      setStatus((s) => ({ ...s, loading: false }));
      return;
    }
    // /gcal/calendars is best-effort — if the token got revoked externally
    // it'll fail; we still want the Disconnect button visible.
    if (st?.connected) {
      try {
        const { data: cals } = await api.get("/gcal/calendars");
        setCalendars(cals || []);
      } catch (_e) {
        setCalendars([]);
      }
    }
  };
  useEffect(() => { load(); }, []);

  // React to OAuth callback query params
  useEffect(() => {
    const g = params.get("gcal");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar connected");
    else if (g === "error") toast.error(`Google Calendar connect failed: ${params.get("reason") || "unknown"}`);
    // Clean the URL
    const next = new URLSearchParams(params);
    next.delete("gcal"); next.delete("email"); next.delete("reason");
    setParams(next, { replace: true });
    load();
  }, [params]);

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/start");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Connect failed"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Existing bookings will remain but no new ones will sync.")) return;
    try {
      await api.post("/gcal/disconnect");
      toast.success("Google Calendar disconnected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  const selectCalendar = async (calendar_id) => {
    setSaving(true);
    try {
      await api.put("/gcal/target-calendar", { calendar_id });
      setStatus((s) => ({ ...s, calendar_id }));
      toast.success("Sync target updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  if (status.loading) return null;

  if (!status.configured) {
    return (
      <div className="brutalist-card p-4 mb-6 border-dashed" data-testid="gcal-not-configured">
        <div className="section-label">Google Calendar</div>
        <p className="text-xs text-[var(--peak-muted)] mt-2">
          Not configured on the server yet. Add <code className="bg-[var(--peak-surface-alt)] px-1">GOOGLE_CLIENT_ID</code> and
          {" "}<code className="bg-[var(--peak-surface-alt)] px-1">GOOGLE_CLIENT_SECRET</code> to <code className="bg-[var(--peak-surface-alt)] px-1">backend/.env</code> to enable 2-way sync.
        </p>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="brutalist-card p-6 mb-6" data-testid="gcal-connect-panel">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 border-2 border-[var(--peak-ink)] flex items-center justify-center bg-[var(--peak-surface-alt)]">
            <Calendar className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="section-label">Google Calendar sync</div>
            <div className="text-sm mt-0.5">Block personal events from being double-booked. Auto-invite attendees.</div>
          </div>
          <button
            onClick={connect}
            data-testid="gcal-connect-btn"
            className="bg-[var(--peak-ink)] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 border-2 border-[var(--peak-ink)] hover:bg-[var(--peak-accent)] hover:border-[var(--peak-accent)] inline-flex items-center gap-2"
          >
            <Link2 className="w-3 h-3" /> Connect Google Calendar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="brutalist-card p-6 mb-6" data-testid="gcal-connected-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 bg-[#008A27] text-white flex items-center justify-center">
          <Check className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="section-label">Google Calendar · connected</div>
          <div className="text-sm font-bold uppercase tracking-widest truncate mt-0.5" data-testid="gcal-email">{status.email}</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="section-label">Write bookings to:</label>
            <select
              value={status.calendar_id || "primary"}
              onChange={(e) => selectCalendar(e.target.value)}
              disabled={saving}
              data-testid="gcal-target-select"
              className="brutalist-input py-1 text-sm"
            >
              {calendars.length === 0 && <option value="primary">primary</option>}
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.summary}{c.primary ? " · primary" : ""}
                </option>
              ))}
            </select>
            <button onClick={load} title="Refresh" className="border border-[var(--peak-ink)] hover:bg-black hover:text-white p-1.5">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        </div>
        <button
          onClick={disconnect}
          data-testid="gcal-disconnect-btn"
          className="border-2 border-[var(--peak-ink)] text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-[#E50000] hover:text-white hover:border-[#E50000] inline-flex items-center gap-1"
        >
          <Unplug className="w-3 h-3" /> Disconnect
        </button>
      </div>
    </div>
  );
}
